import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperAirplaneIcon, SparklesIcon, TrashIcon, PlusIcon, PencilIcon } from '@heroicons/react/24/solid';
import {
  IChatMessage,
  ChatContextType,
  ChatMessageRole,
  LLMStreamEventType,
  SubjectActionCard,
  SubjectActionType,
  SubjectActionTypeLabels,
  SubjectAIResponse,
  AssetType,
  getLabelsByLocale,
  Locale
} from '@packages/global';
import { chatMessageApi, llmApi } from '../../services/user';
import { projectAssetApi } from '../../services/user/projectAsset.api';
import { useToast } from '../../stores/toastStore';
import { useDialog } from '../../hooks/useDialog';

interface SubjectChatPanelProps {
  projectId: string;
  onSubjectsChange: () => void;
}

export default function SubjectChatPanel({ projectId, onSubjectsChange }: SubjectChatPanelProps) {
  const { t, i18n } = useTranslation(['projectSettings', 'common']);
  const subjectActionTypeLabels = getLabelsByLocale(SubjectActionTypeLabels, i18n.language as Locale);
  const toast = useToast();
  const { confirm } = useDialog();
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    loadMessages();
    return () => { abortControllerRef.current?.abort(); };
  }, [projectId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages, streamingContent]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await chatMessageApi.list({
        projectId,
        contextType: ChatContextType.SUBJECT_SETTING,
        limit: 50
      });
      setMessages(data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || streaming) return;
    const userMessage = input.trim();
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setStreaming(true);
    setStreamingContent('');

    const tempUserMsg: IChatMessage = {
      id: `temp-${Date.now()}`,
      projectId,
      contextType: ChatContextType.SUBJECT_SETTING,
      role: ChatMessageRole.USER,
      content: userMessage,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      abortControllerRef.current = new AbortController();
      await llmApi.stream(
        { projectId, contextType: ChatContextType.SUBJECT_SETTING, prompt: userMessage },
        (event) => {
          switch (event.type) {
            case LLMStreamEventType.DELTA:
              setStreamingContent(prev => prev + (event.content || ''));
              break;
            case LLMStreamEventType.DONE:
              const fullContent = event.content || '';
              const aiMsg: IChatMessage = {
                id: `ai-${Date.now()}`,
                projectId,
                contextType: ChatContextType.SUBJECT_SETTING,
                role: ChatMessageRole.ASSISTANT,
                content: fullContent,
                createdAt: new Date(),
                updatedAt: new Date()
              };
              setMessages(prev => [...prev, aiMsg]);
              setStreamingContent('');
              setStreaming(false);
              break;
            case LLMStreamEventType.ERROR:
              toast.error(event.error || t('subjectChat.aiGenerateFailed'));
              setStreaming(false);
              setStreamingContent('');
              break;
          }
        },
        abortControllerRef.current.signal
      );
    } catch (error: any) {
      if (error.name !== 'AbortError') toast.error(t('subjectChat.sendFailed'));
      setStreaming(false);
      setStreamingContent('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = async () => {
    const confirmed = await confirm(t('subjectChat.clearConfirm'), t('subjectChat.clearConfirmTitle'), 'warning');
    if (!confirmed) return;
    try {
      await chatMessageApi.clear(projectId, ChatContextType.SUBJECT_SETTING);
      setMessages([]);
      toast.success(t('subjectChat.chatCleared'));
    } catch { toast.error(t('subjectChat.clearFailed')); }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (messageId.startsWith('temp-') || messageId.startsWith('ai-')) {
      setMessages(prev => prev.filter(m => m.id !== messageId));
      return;
    }
    try {
      await chatMessageApi.delete(messageId);
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch { toast.error(t('subjectChat.deleteFailed')); }
  };

  const handleApplyAction = async (action: SubjectActionCard) => {
    try {
      switch (action.actionType) {
        case SubjectActionType.CREATE:
          const assetTypeValue = action.assetType === 'prop' ? AssetType.PROP
            : action.assetType === 'scene' ? AssetType.SCENE : AssetType.CHARACTER;
          await projectAssetApi.createProjectAsset({
            projectId, name: action.name, assetType: assetTypeValue,
            role: action.role as any, description: action.description
          });
          toast.success(t('subjectChat.subjectAdded', { name: action.name }));
          break;
        case SubjectActionType.UPDATE:
          if (!action.subjectId) { toast.error(t('subjectChat.missingIdUpdate')); return; }
          await projectAssetApi.updateProjectAsset(action.subjectId, {
            name: action.name, role: action.role as any, description: action.description
          });
          toast.success(t('subjectChat.subjectUpdated', { name: action.name }));
          break;
        case SubjectActionType.DELETE:
          if (!action.subjectId) { toast.error(t('subjectChat.missingIdDelete')); return; }
          await projectAssetApi.deleteProjectAsset(action.subjectId);
          toast.success(t('subjectChat.subjectDeleted', { name: action.name }));
          break;
      }
      onSubjectsChange();
    } catch { toast.error(t('subjectChat.actionFailed')); }
  };

  const getActionButtonConfig = (actionType: SubjectActionType) => {
    switch (actionType) {
      case SubjectActionType.CREATE: return { icon: PlusIcon, text: t('subjectChat.addSubject'), color: '#22C55E' };
      case SubjectActionType.UPDATE: return { icon: PencilIcon, text: t('subjectChat.applyUpdate'), color: '#2563EB' };
      case SubjectActionType.DELETE: return { icon: TrashIcon, text: t('subjectChat.deleteSubject'), color: '#EF4444' };
    }
  };

  const formatTime = (date: Date) => {
    const d = new Date(date);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const parseMessageContent = (content: string): { text: string; actions: SubjectActionCard[] } => {
    try {
      const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[1]) as SubjectAIResponse;
        return { text: parsed.message, actions: parsed.actions || [] };
      }
      const trimmed = content.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        const parsed = JSON.parse(trimmed) as SubjectAIResponse;
        return { text: parsed.message, actions: parsed.actions || [] };
      }
      const jsonObjectMatch = content.match(/\{[\s\S]*"message"[\s\S]*\}/);
      if (jsonObjectMatch) {
        const parsed = JSON.parse(jsonObjectMatch[0]) as SubjectAIResponse;
        return { text: parsed.message, actions: parsed.actions || [] };
      }
      return { text: content, actions: [] };
    } catch {
      return { text: content, actions: [] };
    }
  };

  return (
    <div className="h-full flex flex-col bg-bg-page rounded-xl border border-border-subtle overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center">
            <SparklesIcon className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-gray-50">{t('subjectChat.title')}</span>
        </div>
        <div className="flex items-center gap-2">
          {messages.length > 0 && (
            <button
              onClick={handleClear}
              className="p-1.5 text-gray-400 hover:text-red-400 transition-colors cursor-pointer"
              title={t('subjectChat.clearChat')}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
          <div className="flex items-center gap-1.5 px-2 py-1 bg-green-900/30 rounded">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
            <span className="text-[11px] text-green-500 font-medium">{t('subjectChat.online')}</span>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400">
            <span className="text-sm">{t('subjectChat.loading')}</span>
          </div>
        ) : (
          <>
            {/* Welcome message */}
            <div className="flex gap-2.5">
              <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                <SparklesIcon className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="bg-bg-elevated rounded-xl px-3 py-2.5 max-w-[85%]">
                <p className="text-[13px] text-gray-200 leading-relaxed">
                  {t('subjectChat.welcome')}
                </p>
              </div>
            </div>

            {messages.map((msg) => {
              const isUser = msg.role === ChatMessageRole.USER;
              const { text, actions } = isUser ? { text: msg.content, actions: [] } : parseMessageContent(msg.content);

              return (
                <div key={msg.id} className="flex flex-col gap-2">
                  <div className={`group flex gap-2.5 ${isUser ? 'justify-end' : ''}`}>
                    {!isUser && (
                      <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                        <SparklesIcon className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    <div className={`flex flex-col gap-1 max-w-[85%] ${isUser ? 'items-end' : ''}`}>
                      <div className={`rounded-xl px-3 py-2.5 ${isUser ? 'bg-primary/10 border border-primary/40' : 'bg-bg-elevated'}`}>
                        <p className={`text-[13px] leading-relaxed whitespace-pre-wrap break-words ${isUser ? 'text-primary' : 'text-gray-200'}`}>
                          {text.length > 500 ? text.slice(0, 500) + '...' : text}
                        </p>
                      </div>
                      <div className={`flex items-center gap-2 ${isUser ? 'justify-end' : ''}`}>
                        {!isUser && (
                          <button
                            onClick={() => handleDeleteMessage(msg.id)}
                            className="p-0.5 text-gray-500 hover:text-red-400 transition-colors cursor-pointer"
                            title={t('subjectChat.deleteMessage')}
                          >
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        )}
                        <span className={`text-[11px] ${isUser ? 'text-primary/50' : 'text-gray-400'}`}>
                          {formatTime(msg.createdAt)}
                        </span>
                        {isUser && (
                          <button
                            onClick={() => handleDeleteMessage(msg.id)}
                            className="p-0.5 text-gray-500 hover:text-red-400 transition-colors cursor-pointer"
                            title={t('subjectChat.deleteMessage')}
                          >
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Cards */}
                  {!isUser && actions.length > 0 && (
                    <div className="flex flex-col gap-2 ml-9">
                      {actions.map((action, idx) => {
                        const config = getActionButtonConfig(action.actionType);
                        const Icon = config.icon;
                        return (
                          <div key={idx} className="rounded-lg border border-border-default p-3 bg-bg-surface">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span
                                  className="px-2 py-0.5 text-[11px] font-semibold text-white rounded"
                                  style={{ backgroundColor: config.color }}
                                >
                                  {subjectActionTypeLabels[action.actionType]}
                                </span>
                                <span className="text-[13px] font-semibold text-gray-50">{action.name}</span>
                              </div>
                              <button
                                onClick={() => handleApplyAction(action)}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors hover:bg-white/5 cursor-pointer"
                                style={{ borderColor: config.color, color: config.color }}
                              >
                                <Icon className="w-3 h-3" />
                                {config.text}
                              </button>
                            </div>
                            <p className="text-[12px] leading-relaxed line-clamp-3 text-gray-300">{action.description}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Streaming */}
            {streaming && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                  <SparklesIcon className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-bg-elevated rounded-xl px-3 py-2.5 max-w-[85%]">
                  {streamingContent ? (
                    <p className="text-[13px] text-gray-200 leading-relaxed whitespace-pre-wrap break-words">{streamingContent}</p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {[0, 150, 300].map(delay => (
                          <div key={delay} className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: `${delay}ms` }} />
                        ))}
                      </div>
                      <span className="text-[13px] text-gray-400">{t('subjectChat.thinking')}</span>
                    </div>
                  )}
                  {streamingContent && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
                      <span className="text-[11px] text-gray-400">{t('subjectChat.generating')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="flex items-end gap-3 p-4 border-t border-border-subtle flex-shrink-0">
        <div className="flex-1">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={handleKeyDown}
            placeholder={t('subjectChat.inputPlaceholder')}
            className="w-full bg-bg-elevated text-gray-50 text-[13px] rounded-lg px-3.5 py-2.5 resize-none focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-gray-400 overflow-y-auto"
            rows={1}
            disabled={streaming}
          />
        </div>
        <button
          onClick={handleSend}
          disabled={!input.trim() || streaming}
          className="w-10 h-10 bg-primary hover:bg-primary/90 disabled:bg-bg-elevated disabled:text-gray-500 text-white rounded-lg flex items-center justify-center transition-colors flex-shrink-0 cursor-pointer"
        >
          <PaperAirplaneIcon className="w-[18px] h-[18px]" />
        </button>
      </div>
    </div>
  );
}
